import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { Api, RootState } from '@reduxjs/toolkit/query';
import { datadogRum } from '@datadog/browser-rum';

import {
  GetInquiryViewsSchemaResponse,
  providerTypeNameMapping,
} from '~/components/maud/inquiries/InquiryViewsTable/utils';
import { StatusCodes } from '~/lib/constants';
import {
  InquiryStatusValue,
  InquiryViewType,
} from '~/lib/constants/inquiry.constants';
import { CareTeamProviderType } from '~/lib/constants/patients.constants';
import urlConstants from '~/lib/constants/url.constants';
import { joinQueryParams } from '~/lib/util/api';
import {
  setIsConflictErrorModalOpenAction,
  setIsLoadingAction,
  setNumberOfInquiriesAction,
} from '~/store/reducers/inquiryList';

import api, { baseCacheTimeInSeconds } from '../api';
import { CacheTag } from '../CacheTag';
import {
  addAssignment,
  getAssignmentsForRoleWithAssignmentIds,
  getInquiryById,
  removeAssignment,
} from '../inquiryServiceHelper';
import { HttpRequestMethod } from '../..';
import { getUpdateInquiryInvalidationTagsByPathname } from './cacheInvalidation';
import { TreatmentEpisodeProgramDetails } from '~/components/maud/patients/treatmentEpisode/Types';

const {
  createInquiry,
  enroll,
  getInquiries,
  getInquiryViews,
  getInquiryViewsByCode,
  getInquiryViewsSchema,
  inquiryDetail,
  inquirerRoles,
  inquiryStatuses,
  informationGatheredSourcesV2,
  coverageReviewStatuses,
  coverageReviewTypes,
  closedReasons,
  medicalReviewNotClearedReasons,
  medicalReviewStatuses,
  medicalReviewTypes,
  mergeDuplicateContactsAndCoverages,
  hearAboutEquipSourcesV2,
  inquiryStatus,
  patientInquiries,
  inquiryNextContactDate,
  inquiryAssignment,
  inquiryAssignments,
  inquiryListItemById,
  inquiryNextSteps,
  inquiryTargetAdmissionDate,
  inquiryInsuranceReview,
  inquiryInsuranceReviewStatus,
  inquiryReviews,
  inquiryMedicalReviewStatus,
  inquiryMedicalReview,
  inquiryDetailV2,
  patientInquirySummary,
} = urlConstants.inquiry;

interface BaseUpdateInquiryRequestParams {
  inquiryId: string;
  showConflictErrorModal?: boolean;
  version: string;
}

interface UpdateInquiryStatusParams extends BaseUpdateInquiryRequestParams {
  closedReasonId?: string;
  closedReason?: string;
  status: string;
  statusId: string;
  pathname: string;
  patientId: string;
  noShowEmail?: boolean;
}

interface UpdateNextContactDateParams extends BaseUpdateInquiryRequestParams {
  nextContactDate: Date;
}

interface UpdateInquiryAssignmentParams extends BaseUpdateInquiryRequestParams {
  assignment: InquiryListItemAssignment;
  providerType: keyof typeof providerTypeNameMapping; // Display name of target role (e.g., "Admissions Specialist"); not required by API, needed for cache updates
}

interface UpdateInquiryAssignmentsParams
  extends BaseUpdateInquiryRequestParams {
  assignments: Omit<InquiryListItemAssignment, 'assignmentId'>[]; // assignmentId is not used here
}

interface DeleteInquiryAssignmentParams extends BaseUpdateInquiryRequestParams {
  assignmentId: string;
  providerType: string; // Display name of target role (e.g., "Admissions Specialist"); not required by API, needed for cache updates
}

interface UpdateNextStepsParams extends BaseUpdateInquiryRequestParams {
  nextSteps: string;
  shouldInvalidate?: boolean;
}

interface UpdateTargetAdmissionParams extends BaseUpdateInquiryRequestParams {
  targetAdmissionDate: Date;
}

interface UpdateInsuranceReviewParams extends BaseUpdateInquiryRequestParams {
  completedDate?: Date;
  description?: string;
  requestedDate?: Date;
  shouldInvalidate?: boolean;
  status?: EnumerationRequest;
  type?: EnumerationRequest;
}

interface UpdateMedicalReviewParams extends BaseUpdateInquiryRequestParams {
  completedDate?: Date;
  description?: string;
  medicalAppointmentDate?: Date;
  notClearedReason?: EnumerationRequest;
  patientId?: string;
  preliminaryDiagnosis?: DiagnosisCode;
  requestedDate?: Date;
  shouldInvalidate?: boolean;
  status?: EnumerationRequest;
  type?: EnumerationRequest;
}

interface ConfirmPatientParams {
  inquiryId: string;
  patientId: string;
}

interface getInquiryViewsListItemByIdParams {
  inquiryId: string;
  viewCode: string;
}

interface UpdateInquiryParams {
  body: UpdateInquiryRequestV2;
  inquiryId: string;
  pathname: string;
  patientId: string;
  showConflictErrorModal?: boolean;
}

interface EnrollInquiryParams extends BaseUpdateInquiryRequestParams {
  admitDate: string; // YYYY-MM-DD
  treatmentProgramName?: string;
  treatmentEpisodePrograms?: TreatmentEpisodeProgramDetails[];
  pathname?: string;
  patientId?: string;
}

const handleRollbackForConflictStatus = (
  error: any,
  dispatch: ThunkDispatch<any, any, UnknownAction>,
  status: StatusCodes,
  showConflictErrorModal: boolean,
  patches?: { undo: () => void }[],
) => {
  if (error.error?.status === status && showConflictErrorModal) {
    dispatch(setIsConflictErrorModalOpenAction(true));
  }
  if (patches) {
    patches.forEach((patch) => patch.undo());
  }
};

const handleUpdatesRollback = (
  error: any,
  dispatch: ThunkDispatch<any, any, UnknownAction>,
  showConflictErrorModal: boolean,
  patches?: { undo: () => void }[],
) => {
  handleRollbackForConflictStatus(
    error,
    dispatch,
    StatusCodes.CONFLICT,
    showConflictErrorModal,
    patches,
  );
};

export enum EndpointKey {
  ADD_INQUIRY = 'addInquiry',
  ADD_INQUIRY_ASSIGNMENT = 'addInquiryAssignment',
  DELETE_INQUIRY_ASSIGNMENT = 'deleteInquiryAssignment',
  GET_INQUIRIES = 'getInquiriesViews',
  GET_INQUIRY_VIEWS = 'getInquiryViews',
  GET_INQUIRY_VIEWS_SCHEMA = 'getInquiryViewsSchema',
  GET_INQUIRY_DETAIL_BY_ID = 'getInquiryDetailById',
  GET_INQUIRY_LIST_ITEM_BY_ID = 'getInquiryListItemById',
  GET_INQUIRY_VIEWS_LIST_ITEM_BY_ID = 'getInquiryViewsListItemById',
  GET_PATIENT_INQUIRIES = 'getPatientInquiries',
  GET_PATIENT_INQUIRIES_SUMMARY = 'getPatientInquiriesSummary',
  UPDATE_INQUIRY_ASSIGNMENTS = 'updateInquiryAssignments',
  UPDATE_INQUIRY_NEXT_CONTACT_DATE = 'updateInquiryNextContactDate',
  UPDATE_INQUIRY_STATUS = 'updateInquiryStatus',
  GET_INQUIRY_STATUSES = 'getInquiryStatuses',
  GET_CLOSED_REASONS = 'getClosedReasons',
  GET_COVERAGE_REVIEW_STATUSES = 'getCoverageReviewStatuses',
  GET_COVERAGE_REVIEW_TYPES = 'getCoverageReviewTypes',
  GET_HEAR_ABOUT_EQUIP_SOURCES = 'getHearAboutEquipSources',
  GET_INFORMATION_GATHERED_SOURCES = 'getInformationGatheredSources',
  GET_MEDICAL_REVIEW_NOT_CLEARED_REASONS = 'getMedicalReviewNotClearedReasons',
  GET_MEDICAL_REVIEW_STATUSES = 'getMedicalReviewStatuses',
  GET_MEDICAL_REVIEW_TYPES = 'getMedicalReviewTypes',
  GET_INQUIRER_ROLES = 'getInquirerRoles',
  UPDATE_NEXT_STEPS = 'updateNextSteps',
  UPDATE_TARGET_ADMISSION = 'updateTargetAdmission',
  GET_INQUIRY_REVIEWS = 'getInquiryReviews',
  UPDATE_INQUIRY_INSURANCE_REVIEW = 'updateInquiryInsuranceReview',
  UPDATE_INQUIRY_INSURANCE_REVIEW_STATUS = 'updateInquiryInsuranceReviewStatus',
  UPDATE_INQUIRY_MEDICAL_REVIEW = 'updateInquiryMedicalReview',
  UPDATE_INQUIRY_MEDICAL_REVIEW_STATUS = 'updateInquiryMedicalReviewStatus',
  CONFIRM_PATIENT = 'confirmPatient',
  UPDATE_INQUIRY = 'updateInquiry',
  ENROLL_INQUIRY = 'enrollInquiry',
  MERGE_DUPLICATE_CONTACTS_AND_COVERAGES = 'mergeDuplicateContactsAndCoverages',
}

const ENUMERATION_CACHE_EXPIRY_SECONDS = 60 * 60;

/**
 * Apply to current inquiry list cache
 * @param callback A callback used to apply data to cache
 * @param api inquiryApi
 * @param getState getState supplied to onQueryStarted
 * @param dispatch dispatch supplied to onQueryStarted
 */
const applyGetInquiries = (
  callback: (draft: GetInquiriesViewsResponse) => void,
  api: Api<any, any, any, any, any>,
  getState: () => RootState<any, any, any>,
  dispatch: ThunkDispatch<any, any, UnknownAction>,
) => {
  const cachedArgs = api.util.selectCachedArgsForQuery(
    getState(),
    EndpointKey.GET_INQUIRIES,
  );
  return cachedArgs.map((cachedQueryArg) => {
    return dispatch(
      api.util.updateQueryData(
        EndpointKey.GET_INQUIRIES,
        cachedQueryArg,
        (draft: GetInquiriesViewsResponse) => {
          callback(draft);
        },
      ),
    );
  });
};

/**
 * Apply to single inquiry in current inquiry list cache
 * @param inquiryId id of inquiry
 * @param callback A callback used to apply data to cache
 * @param api inquiryApi
 * @param getState getState supplied to onQueryStarted
 * @param dispatch dispatch supplied to onQueryStarted
 */
const applyGetInquiriesInquiry = (
  inquiryId: string,
  callback: (inquiry: InquiryViewsListItemResponse) => void,
  api: Api<any, any, any, any, any>,
  getState: () => RootState<any, any, any>,
  dispatch: ThunkDispatch<any, any, UnknownAction>,
) => {
  return applyGetInquiries(
    (draft) => {
      const inquiry = getInquiryById(inquiryId, draft.data);
      if (inquiry) {
        callback(inquiry);
      }
    },
    api,
    getState,
    dispatch,
  );
};

/**
 * Apply to inquiry in get inquiry by id cache
 * @param inquiryId Inquiry id
 * @param callback A callback used to apply data to cache
 * @param api inquiryApi
 * @param dispatch dispatch supplied to onQueryStarted
 */
const applyGetInquiryById = (
  inquiryId,
  callback: (inquiry: InquiryDetailResponse) => void,
  api: Api<any, any, any, any, any>,
  dispatch: ThunkDispatch<any, any, UnknownAction>,
) => {
  return dispatch(
    api.util.updateQueryData(
      EndpointKey.GET_INQUIRY_DETAIL_BY_ID,
      inquiryId,
      (draft) => callback(draft),
    ),
  );
};

const medicalReviewInvalidationTags = (arg: UpdateMedicalReviewParams) => {
  const tags = arg.shouldInvalidate
    ? [
        { id: arg.inquiryId, type: CacheTag.Inquiry },
        { id: arg.inquiryId, type: CacheTag.InquiryReviews },
      ]
    : [{ id: arg.inquiryId, type: CacheTag.InquiryReviews }];

  if (arg.patientId) {
    tags.push({
      id: arg.patientId,
      type: CacheTag.PatientDiagnoses,
    });
  }

  return tags;
};
const insuranceReviewInvalidationTags = (arg: UpdateInsuranceReviewParams) =>
  arg.shouldInvalidate
    ? [
        { id: arg.inquiryId, type: CacheTag.Inquiry },
        { id: arg.inquiryId, type: CacheTag.InquiryReviews },
      ]
    : [{ id: arg.inquiryId, type: CacheTag.InquiryReviews }];

export const inquiryApi = api.injectEndpoints({
  endpoints: (build) => ({
    [EndpointKey.ADD_INQUIRY]: build.mutation<
      InquiryDetailResponse,
      CreateInquiryRequest
    >({
      query: (createInquiryRequest) => ({
        body: createInquiryRequest,
        method: HttpRequestMethod.POST,
        url: createInquiry,
      }),
    }),
    [EndpointKey.ADD_INQUIRY_ASSIGNMENT]: build.mutation<
      AddInquiryAssignmentResponse,
      UpdateInquiryAssignmentParams
    >({
      invalidatesTags: (result, _, arg) => [
        { id: arg.inquiryId, type: CacheTag.Inquiry },
      ],
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        const { assignment, inquiryId, providerType } = arg;
        try {
          const { data } = await queryFulfilled;
          applyGetInquiriesInquiry(
            inquiryId,
            (inquiry) => {
              const inquiryField = providerTypeNameMapping[providerType];

              if (inquiryField) {
                inquiry[inquiryField] = addAssignment(
                  inquiry[inquiryField],
                  assignment,
                  data.assignment.id,
                );
              } else {
                datadogRum.addError(
                  new Error('Failed to map providerType to cache field'),
                  {
                    extra: {
                      assignmentId: data.assignment.id,
                      availableMappings: Object.keys(providerTypeNameMapping),
                      inquiryId,
                      providerType,
                    },
                    tags: { logFrom: 'ADD_INQUIRY_ASSIGNMENT' },
                  },
                );
              }

              inquiry.version = data.version;
            },
            inquiryApi,
            getState,
            dispatch,
          );
        } catch (error) {
          handleUpdatesRollback(error, dispatch, true);
        }
      },
      query: ({ assignment, inquiryId, version }) => ({
        body: {
          userId: assignment.externalId,
          version,
        },
        method: HttpRequestMethod.POST,
        url: inquiryAssignments(inquiryId),
      }),
    }),
    [EndpointKey.DELETE_INQUIRY_ASSIGNMENT]: build.mutation<
      DeleteInquiryAssignmentResponse,
      DeleteInquiryAssignmentParams
    >({
      invalidatesTags: (result, _, arg) => [
        { id: arg.inquiryId, type: CacheTag.Inquiry },
      ],
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        const { assignmentId, inquiryId, providerType } = arg;

        try {
          const { data } = await queryFulfilled;
          applyGetInquiriesInquiry(
            inquiryId,
            (inquiry) => {
              const inquiryField = providerTypeNameMapping[providerType];

              if (inquiryField) {
                inquiry[inquiryField] = removeAssignment(
                  assignmentId,
                  inquiry[inquiryField],
                );
              } else {
                datadogRum.addError(
                  new Error('Failed to map providerType to cache field'),
                  {
                    extra: {
                      assignmentId,
                      availableMappings: Object.keys(providerTypeNameMapping),
                      inquiryId,
                      providerType,
                    },
                    tags: { logFrom: 'DELETE_INQUIRY_ASSIGNMENT' },
                  },
                );
              }

              inquiry.version = data.version;
            },
            inquiryApi,
            getState,
            dispatch,
          );
        } catch (error) {
          handleRollbackForConflictStatus(
            error,
            dispatch,
            StatusCodes.NOT_FOUND,
            true,
          );
        }
      },
      query: ({ assignmentId, inquiryId, version }) => ({
        body: {
          version,
        },
        method: HttpRequestMethod.DELETE,
        url: inquiryAssignment(inquiryId, assignmentId),
      }),
    }),
    [EndpointKey.GET_INQUIRIES]: build.query<
      GetInquiriesViewsResponse,
      GetInquiriesRequest
    >({
      keepUnusedDataFor: baseCacheTimeInSeconds,
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        dispatch(setIsLoadingAction(true));
        try {
          const { data } = await queryFulfilled;
          dispatch(setNumberOfInquiriesAction(data.totalCount));
        } catch (error) {
          dispatch(setNumberOfInquiriesAction(undefined));
        } finally {
          dispatch(setIsLoadingAction(false));
        }
      },
      providesTags: (result) => {
        if (!result || !result.data || result.data.length === 0) {
          return [CacheTag.Inquiries];
        }

        const inquiryTags = result.data.map((inquiry) => ({
          id: inquiry.inquiryId,
          type: CacheTag.Inquiries,
        }));

        return [CacheTag.Inquiries, ...inquiryTags];
      },
      query: ({
        filterBy,
        orderBy,
        orderByDirection,
        pageNumber,
        pageSize,
        viewCode,
        includeAvatar,
      }) => {
        return {
          params: {
            'filter-by': joinQueryParams(filterBy),
            'order-by': orderBy,
            'order-by-direction': orderByDirection,
            'page-number': pageNumber,
            'page-size': pageSize,
            'include-avatar': includeAvatar,
          },
          url: getInquiryViewsByCode(viewCode),
        };
      },
    }),
    [EndpointKey.GET_INQUIRY_VIEWS]: build.query<GetInquiryViewsResponse, void>(
      {
        query: () => getInquiryViews,
      },
    ),
    [EndpointKey.GET_INQUIRY_VIEWS_SCHEMA]: build.query<
      GetInquiryViewsSchemaResponse,
      string
    >({
      query: (code: InquiryViewType) => getInquiryViewsSchema(code),
    }),
    [EndpointKey.GET_INQUIRY_DETAIL_BY_ID]: build.query<
      InquiryDetailResponse,
      string
    >({
      providesTags: (result, error, inquiryId) => [
        { id: inquiryId, type: CacheTag.Inquiry },
      ],
      query: (id) => inquiryDetail(id),
    }),
    [EndpointKey.GET_INQUIRY_LIST_ITEM_BY_ID]: build.query<
      InquiryViewsListItemResponse,
      string
    >({
      keepUnusedDataFor: 0,
      onQueryStarted: async (
        inquiryId,
        { dispatch, queryFulfilled, getState },
      ) => {
        try {
          const { data } = await queryFulfilled;

          applyGetInquiries(
            (draft) => {
              draft.data = draft.data.map((inquiry) => {
                if (inquiry.inquiryId === inquiryId) {
                  return data;
                }
                return inquiry;
              });
            },
            inquiryApi,
            getState,
            dispatch,
          );
        } catch (error) {
          console.error(error);
        }
      },
      providesTags: (result, error, inquiryId) => [
        { id: inquiryId, type: CacheTag.Inquiry },
      ],
      query: (id) => `${getInquiries}/${id}`,
    }),
    [EndpointKey.GET_INQUIRY_VIEWS_LIST_ITEM_BY_ID]: build.query<
      InquiryViewsListItemResponse,
      getInquiryViewsListItemByIdParams
    >({
      providesTags: (result, error, arg) => [
        { id: arg.inquiryId, type: CacheTag.Inquiry },
      ],
      query: ({ inquiryId, viewCode }) =>
        inquiryListItemById(inquiryId, viewCode),
    }),
    [EndpointKey.GET_PATIENT_INQUIRIES]: build.query<
      GetPatientInquiriesResponse[],
      string
    >({
      providesTags: (result, error, patientId) => [
        { id: patientId, type: CacheTag.PatientInquiries },
      ],
      query: (patientId) => patientInquiries(patientId),
    }),

    [EndpointKey.GET_PATIENT_INQUIRIES_SUMMARY]: build.query<
      GetPatientInquiriesSummaryResponse[],
      string
    >({
      providesTags: (result, error, patientId) => [
        { id: patientId, type: CacheTag.PatientInquiriesSummary },
      ],
      query: patientInquirySummary,
    }),

    [EndpointKey.UPDATE_INQUIRY_ASSIGNMENTS]: build.mutation<
      UpdateInquiryAssignmentsResponse,
      UpdateInquiryAssignmentsParams
    >({
      invalidatesTags: (result, _, arg) => [
        { id: arg.inquiryId, type: CacheTag.Inquiry },
      ],
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        const { assignments, inquiryId } = arg;

        try {
          const { data } = await queryFulfilled;

          applyGetInquiriesInquiry(
            inquiryId,
            (inquiry) => {
              inquiry.version = data.version;

              Object.entries(providerTypeNameMapping).forEach(
                ([role, inquiryField]) => {
                  inquiry[inquiryField] =
                    getAssignmentsForRoleWithAssignmentIds(
                      assignments,
                      data,
                      role as CareTeamProviderType,
                    );
                },
              );
            },
            inquiryApi,
            getState,
            dispatch,
          );
        } catch (error) {}
      },
      query: ({ assignments, inquiryId, version }) => ({
        body: {
          assignments: assignments.map((psr) => ({
            userId: psr.externalId,
          })),
          version,
        },
        method: HttpRequestMethod.PUT,
        url: inquiryAssignments(inquiryId),
      }),
    }),
    [EndpointKey.UPDATE_INQUIRY_NEXT_CONTACT_DATE]: build.mutation<
      VersionedResponse,
      UpdateNextContactDateParams
    >({
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        const { inquiryId, nextContactDate, showConflictErrorModal } = arg;

        const patches = applyGetInquiryById(
          inquiryId,
          (inquiry) => {
            inquiry.nextContactDate = nextContactDate.toISOString();
          },
          api,
          dispatch,
        );

        try {
          const { data } = await queryFulfilled;

          applyGetInquiriesInquiry(
            inquiryId,
            (inquiry) => {
              inquiry.nextContactDate = nextContactDate.toISOString();
              inquiry.version = data.version;
            },
            inquiryApi,
            getState,
            dispatch,
          );

          applyGetInquiryById(
            inquiryId,
            (inquiry) => {
              inquiry.nextContactDate = nextContactDate.toISOString();
              inquiry.version = data.version;
            },
            api,
            dispatch,
          );
        } catch (error) {
          handleUpdatesRollback(error, dispatch, showConflictErrorModal, [
            patches,
          ]);
        }
      },
      query: ({ inquiryId, nextContactDate, version }) => ({
        body: {
          nextContactDate,
          version,
        },
        method: HttpRequestMethod.PUT,
        url: inquiryNextContactDate(inquiryId),
      }),
    }),
    [EndpointKey.UPDATE_INQUIRY_STATUS]: build.mutation<
      VersionedResponse,
      UpdateInquiryStatusParams
    >({
      invalidatesTags: (result, error, arg) => {
        const { inquiryId, patientId, pathname } = arg;
        const invalidationTags = getUpdateInquiryInvalidationTagsByPathname(
          pathname,
          inquiryId,
          patientId,
        );
        return invalidationTags;
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        const { inquiryId, status, showConflictErrorModal } = arg;

        try {
          const { data } = await queryFulfilled;

          applyGetInquiriesInquiry(
            inquiryId,
            (inquiry) => {
              inquiry.version = data.version;
              if (status && inquiry.inquiryStatus)
                inquiry.inquiryStatus = status;
            },
            inquiryApi,
            getState,
            dispatch,
          );

          applyGetInquiryById(
            inquiryId,
            (inquiry) => {
              inquiry.version = data.version;
              if (status && inquiry.status) {
                inquiry.status.description = status;
                inquiry.status.name = status;
              }
            },
            api,
            dispatch,
          );
        } catch (error) {
          handleUpdatesRollback(error, dispatch, showConflictErrorModal);
        }
      },
      query: ({
        closedReasonId,
        statusId,
        inquiryId,
        version,
        noShowEmail,
      }) => {
        return {
          body: {
            closedReasonId,
            noShowEmail,
            statusId,
            version,
          },
          method: HttpRequestMethod.PUT,
          url: inquiryStatus(inquiryId),
        };
      },
    }),
    [EndpointKey.GET_INQUIRY_STATUSES]: build.query<InquiryEnum[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_EXPIRY_SECONDS,
      query: () => inquiryStatuses,
    }),
    [EndpointKey.GET_CLOSED_REASONS]: build.query<InquiryEnum[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_EXPIRY_SECONDS,
      query: () => closedReasons,
    }),
    [EndpointKey.GET_COVERAGE_REVIEW_STATUSES]: build.query<
      InquiryEnumV2[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_EXPIRY_SECONDS,
      query: () => coverageReviewStatuses,
    }),
    [EndpointKey.GET_COVERAGE_REVIEW_TYPES]: build.query<InquiryEnumV2[], void>(
      {
        keepUnusedDataFor: ENUMERATION_CACHE_EXPIRY_SECONDS,
        query: () => coverageReviewTypes,
      },
    ),
    [EndpointKey.GET_HEAR_ABOUT_EQUIP_SOURCES]: build.query<
      EquipSourceV2[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_EXPIRY_SECONDS,
      query: () => hearAboutEquipSourcesV2,
    }),
    [EndpointKey.GET_INFORMATION_GATHERED_SOURCES]: build.query<
      InquiryEnum[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_EXPIRY_SECONDS,
      query: () => informationGatheredSourcesV2,
    }),
    [EndpointKey.GET_MEDICAL_REVIEW_NOT_CLEARED_REASONS]: build.query<
      InquiryEnumV2[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_EXPIRY_SECONDS,
      query: () => medicalReviewNotClearedReasons,
    }),
    [EndpointKey.GET_MEDICAL_REVIEW_STATUSES]: build.query<
      InquiryEnumV2[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_EXPIRY_SECONDS,
      query: () => medicalReviewStatuses,
    }),
    [EndpointKey.GET_MEDICAL_REVIEW_TYPES]: build.query<InquiryEnumV2[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_EXPIRY_SECONDS,
      query: () => medicalReviewTypes,
    }),
    [EndpointKey.GET_INQUIRER_ROLES]: build.query<InquiryEnum[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_EXPIRY_SECONDS,
      query: () => inquirerRoles,
    }),
    [EndpointKey.UPDATE_NEXT_STEPS]: build.mutation<
      VersionedResponse,
      UpdateNextStepsParams
    >({
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        const { inquiryId, showConflictErrorModal, nextSteps } = arg;

        try {
          const { data } = await queryFulfilled;

          applyGetInquiriesInquiry(
            inquiryId,
            (inquiry) => {
              inquiry.nextSteps = nextSteps;
              inquiry.version = data.version;
            },
            inquiryApi,
            getState,
            dispatch,
          );

          applyGetInquiryById(
            inquiryId,
            (inquiry) => {
              inquiry.nextSteps = nextSteps;
              inquiry.version = data.version;
            },
            api,
            dispatch,
          );
        } catch (error) {
          handleUpdatesRollback(error, dispatch, showConflictErrorModal);
        }
      },
      query: ({ inquiryId, nextSteps, version }) => ({
        body: {
          nextSteps,
          version,
        },
        method: HttpRequestMethod.PUT,
        url: inquiryNextSteps(inquiryId),
      }),
    }),
    [EndpointKey.UPDATE_TARGET_ADMISSION]: build.mutation<
      VersionedResponse,
      UpdateTargetAdmissionParams
    >({
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        const { inquiryId, targetAdmissionDate, showConflictErrorModal } = arg;

        const patches = applyGetInquiryById(
          inquiryId,
          (inquiry) => {
            inquiry.targetAdmissionDate = targetAdmissionDate.toISOString();
          },
          api,
          dispatch,
        );
        try {
          const { data } = await queryFulfilled;

          applyGetInquiriesInquiry(
            inquiryId,
            (inquiry) => {
              inquiry.version = data.version;
              inquiry.targetAdmissionDate = targetAdmissionDate.toISOString();
            },
            inquiryApi,
            getState,
            dispatch,
          );

          applyGetInquiryById(
            inquiryId,
            (inquiry) => {
              inquiry.version = data.version;
              inquiry.targetAdmissionDate = targetAdmissionDate.toISOString();
            },
            api,
            dispatch,
          );
        } catch (error) {
          handleUpdatesRollback(error, dispatch, showConflictErrorModal, [
            patches,
          ]);
        }
      },
      query: ({ inquiryId, targetAdmissionDate, version }) => ({
        body: {
          targetAdmissionDate,
          version,
        },
        method: HttpRequestMethod.PUT,
        url: inquiryTargetAdmissionDate(inquiryId),
      }),
    }),
    [EndpointKey.GET_INQUIRY_REVIEWS]: build.query<
      InquiryReviewsResponse,
      string
    >({
      providesTags: (result, error, inquiryId) => [
        { id: inquiryId, type: CacheTag.InquiryReviews },
      ],
      query: (inquiryId: string) => inquiryReviews(inquiryId),
    }),
    [EndpointKey.UPDATE_INQUIRY_INSURANCE_REVIEW]: build.mutation<
      VersionedResponse,
      UpdateInsuranceReviewParams
    >({
      invalidatesTags: (result, _, arg) => insuranceReviewInvalidationTags(arg),
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        const { description, inquiryId, showConflictErrorModal } = arg;

        try {
          const { data } = await queryFulfilled;

          applyGetInquiriesInquiry(
            inquiryId,
            (inquiry) => {
              if (description && inquiry.coverageReview)
                inquiry.coverageReview.description = description;
              inquiry.version = data.version;
            },
            inquiryApi,
            getState,
            dispatch,
          );

          applyGetInquiryById(
            inquiryId,
            (inquiry) => {
              inquiry.version = data.version;
            },
            inquiryApi,
            dispatch,
          );
        } catch (error) {
          handleUpdatesRollback(error, dispatch, showConflictErrorModal);
        }
      },
      query: (args) => ({
        body: args,
        method: HttpRequestMethod.PUT,
        url: inquiryInsuranceReview(args.inquiryId),
      }),
    }),
    [EndpointKey.UPDATE_INQUIRY_INSURANCE_REVIEW_STATUS]: build.mutation<
      VersionedResponse,
      UpdateInsuranceReviewParams
    >({
      invalidatesTags: (result, _, arg) => insuranceReviewInvalidationTags(arg),
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        const { description, inquiryId, showConflictErrorModal } = arg;

        try {
          const { data } = await queryFulfilled;

          applyGetInquiriesInquiry(
            inquiryId,
            (inquiry) => {
              inquiry.version = data.version;
              if (description && inquiry.coverageReview)
                inquiry.coverageReview.description = description;
            },
            inquiryApi,
            getState,
            dispatch,
          );
          applyGetInquiryById(
            inquiryId,
            (inquiry) => {
              inquiry.version = data.version;
            },
            inquiryApi,
            dispatch,
          );
        } catch (error) {
          handleUpdatesRollback(error, dispatch, showConflictErrorModal);
        }
      },
      query: (args) => ({
        body: args,
        method: HttpRequestMethod.PUT,
        url: inquiryInsuranceReviewStatus(args.inquiryId),
      }),
    }),
    [EndpointKey.UPDATE_INQUIRY_MEDICAL_REVIEW_STATUS]: build.mutation<
      VersionedResponse,
      UpdateMedicalReviewParams
    >({
      invalidatesTags: (result, _, arg) => medicalReviewInvalidationTags(arg),
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        const { inquiryId, showConflictErrorModal, description } = arg;

        try {
          const { data } = await queryFulfilled;

          applyGetInquiriesInquiry(
            inquiryId,
            (inquiry) => {
              inquiry.version = data.version;
              if (description && inquiry.medicalReview)
                inquiry.medicalReview.description = description;
            },
            inquiryApi,
            getState,
            dispatch,
          );
          applyGetInquiryById(
            inquiryId,
            (inquiry) => {
              inquiry.version = data.version;
            },
            inquiryApi,
            dispatch,
          );
        } catch (error) {
          handleUpdatesRollback(error, dispatch, showConflictErrorModal);
        }
      },
      query: (args) => ({
        body: args,
        method: HttpRequestMethod.PUT,
        url: inquiryMedicalReviewStatus(args.inquiryId),
      }),
    }),
    [EndpointKey.UPDATE_INQUIRY_MEDICAL_REVIEW]: build.mutation<
      VersionedResponse,
      UpdateMedicalReviewParams
    >({
      invalidatesTags: (result, _, arg) => medicalReviewInvalidationTags(arg),
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        const {
          inquiryId,
          showConflictErrorModal,
          description,
          preliminaryDiagnosis,
        } = arg;

        try {
          const { data } = await queryFulfilled;

          applyGetInquiriesInquiry(
            inquiryId,
            (inquiry) => {
              inquiry.version = data.version;
              if (description && inquiry.medicalReview)
                inquiry.medicalReview.description = description;
              if (preliminaryDiagnosis)
                inquiry.diagnosisShortDescription =
                  preliminaryDiagnosis.shortDescription;
            },
            inquiryApi,
            getState,
            dispatch,
          );
          applyGetInquiryById(
            inquiryId,
            (inquiry) => {
              inquiry.version = data.version;
            },
            inquiryApi,
            dispatch,
          );
        } catch (error) {
          handleUpdatesRollback(error, dispatch, showConflictErrorModal);
        }
      },
      query: (args) => ({
        body: args,
        method: HttpRequestMethod.PUT,
        url: inquiryMedicalReview(args.inquiryId),
      }),
    }),
    [EndpointKey.CONFIRM_PATIENT]: build.mutation<void, ConfirmPatientParams>({
      invalidatesTags: (result, _, arg) => [
        { id: arg.inquiryId, type: CacheTag.Inquiry },
        { id: arg.patientId, type: CacheTag.PatientContactById },
      ],
      query: ({ inquiryId, patientId }) => ({
        body: { patientId },
        method: HttpRequestMethod.PUT,
        url: urlConstants.inquiry.confirmPatient(inquiryId),
      }),
    }),
    [EndpointKey.UPDATE_INQUIRY]: build.mutation<
      VersionedResponse,
      UpdateInquiryParams
    >({
      invalidatesTags: (result, error, arg) => {
        const { inquiryId, patientId, pathname } = arg;
        const invalidationTags = getUpdateInquiryInvalidationTagsByPathname(
          pathname,
          inquiryId,
          patientId,
        );
        return invalidationTags;
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        const { body, inquiryId, showConflictErrorModal } = arg;

        try {
          const { data } = await queryFulfilled;
          applyGetInquiriesInquiry(
            inquiryId,
            (inquiry) => {
              inquiry.version = data.version;
              inquiry.preferredLanguage = body?.patient?.languages[0];
              inquiry.payor.name = body?.coverages[0]?.payor;
              inquiry.patient = {
                ...body?.patient,
                firstName: body?.patient?.firstName || '',
                id: body?.patient?.id || '',
                lastName: body?.patient?.lastName || '',
              };
              inquiry.timezone = body?.patient?.timezone;
              const primaryContact = body?.patientContacts.find(
                (c) => c.isPrimaryContact,
              );
              inquiry.primaryContact = {
                externalId: primaryContact?.id || '',
                firstName: primaryContact?.firstName || '',
                lastName: primaryContact?.lastName || '',
                relationToPatient: primaryContact?.relationToPatient || '',
                ...primaryContact,
              };
              inquiry.inquiryDate = body?.inquiryDate;
            },
            inquiryApi,
            getState,
            dispatch,
          );
          applyGetInquiryById(
            inquiryId,
            (inquiry) => {
              inquiry.version = data.version;
            },
            inquiryApi,
            dispatch,
          );
        } catch (error) {
          handleUpdatesRollback(error, dispatch, showConflictErrorModal);
        }
      },
      query: ({ inquiryId, body }) => ({
        body,
        method: HttpRequestMethod.PUT,
        url: inquiryDetailV2(inquiryId),
      }),
    }),
    [EndpointKey.ENROLL_INQUIRY]: build.mutation<
      VersionedResponse,
      EnrollInquiryParams
    >({
      invalidatesTags: (result, error, arg) => {
        const { inquiryId, patientId, pathname } = arg;
        const invalidationTags = getUpdateInquiryInvalidationTagsByPathname(
          pathname,
          inquiryId,
          patientId,
        );
        return invalidationTags;
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        const { inquiryId, showConflictErrorModal } = arg;

        try {
          const { data } = await queryFulfilled;

          applyGetInquiriesInquiry(
            inquiryId,
            (inquiry) => {
              inquiry.version = data.version;
              inquiry.inquiryStatus = InquiryStatusValue.ENROLLED;
            },
            inquiryApi,
            getState,
            dispatch,
          );

          applyGetInquiryById(
            inquiryId,
            (inquiry) => {
              inquiry.version = data.version;
              if (inquiry.status) {
                inquiry.status.description = InquiryStatusValue.ENROLLED;
                inquiry.status.name = InquiryStatusValue.ENROLLED;
              }
            },
            api,
            dispatch,
          );
        } catch (error) {
          handleUpdatesRollback(error, dispatch, showConflictErrorModal);
        }
      },
      query: ({ admitDate, inquiryId, treatmentEpisodePrograms, version }) => ({
        body: {
          admitDate,
          treatmentEpisodePrograms,
          version,
        },
        method: HttpRequestMethod.POST,
        url: enroll(inquiryId),
      }),
    }),
    [EndpointKey.MERGE_DUPLICATE_CONTACTS_AND_COVERAGES]: build.mutation<
      VersionedResponse,
      MergeDuplicateContactsAndCoveragesRequestParams
    >({
      invalidatesTags: (result, error, arg) => [
        { id: arg.inquiryId, type: CacheTag.PatientContactById },
      ],
      query: ({ inquiryId, body }) => ({
        body,
        method: HttpRequestMethod.PATCH,
        url: mergeDuplicateContactsAndCoverages(inquiryId),
      }),
    }),
  }),
});

export const {
  useAddInquiryAssignmentMutation,
  useAddInquiryMutation,
  useConfirmPatientMutation,
  useDeleteInquiryAssignmentMutation,
  useEnrollInquiryMutation,
  useGetInquiriesViewsQuery,
  useGetInquiryViewsQuery,
  useGetInquiryViewsSchemaQuery,
  useGetPatientInquiriesQuery,
  useGetPatientInquiriesSummaryQuery,
  useGetInquiryDetailByIdQuery,
  useUpdateInquiryAssignmentsMutation,
  useUpdateInquiryNextContactDateMutation,
  useUpdateInquiryStatusMutation,
  useGetInquirerRolesQuery,
  useGetInquiryStatusesQuery,
  useGetClosedReasonsQuery,
  useGetCoverageReviewStatusesQuery,
  useGetCoverageReviewTypesQuery,
  useGetHearAboutEquipSourcesQuery,
  useGetInformationGatheredSourcesQuery,
  useGetInquiryReviewsQuery,
  useGetMedicalReviewNotClearedReasonsQuery,
  useGetMedicalReviewStatusesQuery,
  useGetMedicalReviewTypesQuery,
  useUpdateInquiryInsuranceReviewMutation,
  useUpdateInquiryInsuranceReviewStatusMutation,
  useUpdateInquiryMedicalReviewStatusMutation,
  useUpdateInquiryMedicalReviewMutation,
  useUpdateInquiryMutation,
  useUpdateNextStepsMutation,
  useUpdateTargetAdmissionMutation,
  useMergeDuplicateContactsAndCoveragesMutation,
} = inquiryApi;
